/**
 * <lih-product-tabs> — swaps a real product section in and out under a tab row.
 *
 * Each tab fetches /products/<handle>?section_id=<id> through the Section
 * Rendering API, so the gallery, variant picker, price and add-to-cart are the
 * live PDP markup rather than a copy that drifts from it.
 *
 * Markup contract:
 *   <lih-product-tabs data-section-id="lp_default_pdp_info_nbaEHK">
 *     <button data-lih-tab data-lih-product="123" data-url="/products/handle"></button>
 *     <div data-lih-panel></div>
 *   </lih-product-tabs>
 *
 * Cards elsewhere: <a href="..." data-lih-open-tab data-lih-product="123">
 */
class LihProductTabs extends HTMLElement {
  /* Script srcs are shared across every instance and every tab — a given file
     must only ever be added to the page once. */
  static loaded = new Set();

  /* Root element every pdp-info variant renders, and what the gallery script
     itself looks for — stable even though the section type differs by
     product template. */
  static SELECTOR = '.lp-pdp-engraved-locket';

  /* Right hand details column of the product section. */
  static COLUMN = '.lp-pdp__info-container';

  /* The tabs only belong in that column where it sits beside the gallery. On
     narrow screens the column stacks underneath, so moving them there would put
     the row below the image instead of above it. */
  static COLUMN_LAYOUT = '(min-width: 768px)';

  connectedCallback() {
    this.panel = this.querySelector('[data-lih-panel]');
    this.list = this.querySelector('[data-lih-list]');
    if (!this.panel || !this.list || !this.list.querySelector('[data-lih-tab]')) return;

    this.sectionId = this.dataset.sectionId;
    this.remove = this.dataset.remove;
    this.cache = new Map();
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.list.addEventListener('click', (event) => {
      const tab = event.target.closest('[data-lih-tab]');
      if (tab && this.list.contains(tab)) this.select(tab, tab.dataset.url, { focus: true });
    });
    this.list.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick, true);

    /* Crossing the breakpoint changes where the row belongs, and resizing does
       not re-run select(). */
    this.columnLayout = window.matchMedia(LihProductTabs.COLUMN_LAYOUT);
    this.onLayoutChange = () => this.mountTabs();
    this.columnLayout.addEventListener('change', this.onLayoutChange);

    this.ensureStyles();
    this.select(this.list.querySelector('[data-lih-tab]'));
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
    this.list?.removeEventListener('keydown', this.onKeydown);
    this.columnLayout?.removeEventListener('change', this.onLayoutChange);
  }

  onKeydown(event) {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      return;
    }

    const tabs = this.tabs();
    const current = tabs.indexOf(event.target.closest('[data-lih-tab]'));
    if (current < 0) return;

    event.preventDefault();
    let next = current;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      next = (current + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      next = (current - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      next = 0;
    } else {
      next = tabs.length - 1;
    }

    this.select(tabs[next], tabs[next].dataset.url, { focus: true });
  }

  /* Product cards use data-lih-open-tab. Never leave the page: scroll here,
     select the tab with the same product id, and load that product. If no tab
     exists for that id, the card's product is still loaded into the panel. */
  onDocumentClick(event) {
    /* A plain jump: scroll here and leave the open tab as it is. */
    const jump = event.target.closest('[data-lih-goto-tabs]');
    if (jump && !this.contains(jump) && !event.defaultPrevented) {
      event.preventDefault();
      this.scrollHere();
      return;
    }

    const trigger = event.target.closest('[data-lih-open-tab]');
    if (
      !trigger ||
      this.contains(trigger) ||
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const url = trigger.getAttribute('href');
    if (!url) return;

    event.preventDefault();
    this.select(this.tabFor(trigger), url, { focus: true });
    this.scrollHere();
  }

  scrollHere() {
    this.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  tabs() {
    return Array.from(this.list.querySelectorAll('[data-lih-tab]'));
  }

  /* Exact product id first; exact handle only if ids are missing. */
  tabFor(trigger) {
    const id = trigger.dataset.lihProduct;
    if (id) {
      const match = this.tabs().find((tab) => tab.dataset.lihProduct === id);
      if (match) return match;
    }

    const handle = LihProductTabs.handle(trigger.getAttribute('href'));
    if (!handle) return null;
    return this.tabs().find((tab) => LihProductTabs.handle(tab.dataset.url) === handle) || null;
  }

  /* Product handle from a relative or absolute URL, ignoring locale prefixes,
     queries and trailing slashes. */
  static handle(href) {
    if (!href) return '';
    const path = href.split('?')[0].split('#')[0].replace(/\/$/, '');
    const marker = '/products/';
    const index = path.lastIndexOf(marker);
    if (index === -1) return path;
    return decodeURIComponent(path.slice(index + marker.length).split('/')[0]);
  }

  /* The fetched product section carries its own stylesheets, which only begin
     loading once its markup is already in the DOM — long enough for the drawers
     and modals inside it to flash unstyled. Adding them to <head> up front means
     injected markup is styled on its first frame. */
  ensureStyles() {
    const hrefs = (this.dataset.styles || '')
      .split(',')
      .map((href) => href.trim())
      .filter(Boolean);

    for (const href of hrefs) {
      this.adoptStylesheet(href);
    }
  }

  /* Adds one stylesheet to <head> if it is not already there. Compared on the
     resolved URL so the same file written as a relative and an absolute href
     is not loaded twice. */
  adoptStylesheet(href) {
    if (!href) return;

    const url = new URL(href, document.baseURI).href;
    const present = Array.from(
      document.querySelectorAll('link[rel="stylesheet"][href]'),
      (link) => link.href
    );
    if (present.includes(url)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  mark(tab) {
    for (const other of this.tabs()) {
      const on = other === tab;
      other.setAttribute('aria-selected', on ? 'true' : 'false');
      other.tabIndex = on ? 0 : -1;
    }
    if (tab?.id) this.panel.setAttribute('aria-labelledby', tab.id);
    this.revealTab(tab);
  }

  /* The row scrolls on narrow screens, so a tab chosen from a card further
     down the page can sit off the end of it. Scroll the list itself rather
     than scrollIntoView, which would take the page with it and undo the
     scroll to this section. Centring is clamped at both ends, so the first
     and last tabs still come to rest flush. Called with nothing, it re-finds
     whichever tab is selected. */
  revealTab(tab) {
    const target = tab || this.list?.querySelector('[aria-selected="true"]');
    if (!target || !this.list) return;

    const list = this.list.getBoundingClientRect();
    const rect = target.getBoundingClientRect();

    this.list.scrollTo({
      left: this.list.scrollLeft + (rect.left - list.left) - (list.width - rect.width) / 2,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  focusTab(tab) {
    if (!tab) return;
    tab.focus({ preventScroll: true });
  }

  async select(tab, url = tab?.dataset.url, options = {}) {
    if (!url) return;
    const key = tab?.dataset.lihProduct || LihProductTabs.handle(url) || url;

    if (this.current === key) {
      this.mark(tab);
      if (options.focus) this.focusTab(tab);
      return;
    }
    this.current = key;

    /* Mark before the request, not after it. The fallback path fetches a whole
       product page, so waiting on the network left the previously selected tab
       highlighted for the best part of a second while the panel had already
       been asked to change. */
    this.mark(tab);
    this.setAttribute('data-loading', 'true');
    this.panel.setAttribute('aria-busy', 'true');

    try {
      if (!this.cache.has(key)) this.cache.set(key, await this.load(url));

      /* Guard against a slow first request landing after a later tab won. */
      if (this.current !== key) return;

      /* Park the list so innerHTML cannot destroy it, then put it back and
         apply the selected state on the visible buttons. */
      if (this.list) this.append(this.list);
      this.panel.innerHTML = this.cache.get(key);
      this.hoistStyles();
      this.mountTabs();
      await this.activateScripts();
      if (options.focus) this.focusTab(tab);

      /* Scripts that bound to DOMContentLoaded cannot see markup added this
         late, so tell them to run against what was just inserted. */
      document.dispatchEvent(new CustomEvent('lih:pdp-loaded', { detail: { url } }));
    } catch (error) {
      this.cache.delete(key);
      this.current = null;
      this.panel.replaceChildren(this.fallbackLink(tab, url));
      if (options.focus) this.focusTab(tab);
    } finally {
      this.removeAttribute('data-loading');
      this.panel.removeAttribute('aria-busy');
    }
  }

  /**
   * Section Rendering API first — it returns just this one section, so it is
   * far cheaper than a page load. It only works when the section id exists in
   * the template the product resolves to, which is not guaranteed across the
   * store's several product templates, so a miss falls back to fetching the
   * product page. Both results are parsed the same way from there.
   */
  async load(url) {
    let markup = null;

    if (this.sectionId) {
      const response = await fetch(`${url}?section_id=${encodeURIComponent(this.sectionId)}`);
      if (response.ok) {
        const html = await response.text();
        if (html.includes(LihProductTabs.SELECTOR.slice(1))) markup = html;
      }
    }

    if (markup === null) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`${response.status}`);
      markup = await response.text();
    }

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const root = doc.querySelector(LihProductTabs.SELECTOR);
    if (!root) throw new Error('product section not found');

    /* Only the section's own markup is taken, never its shopify-section wrapper.
       That wrapper is what made the panel contain a second section — the tree
       the theme editor reports as broken HTML — and it also duplicated an id the
       editor indexes sections by. The tags it used to be kept for are handled
       elsewhere now: the stylesheets by ensureStyles(), the inline <style> by
       hoistStyles(), so nothing is lost by leaving it behind.

       The <link>, <style> and <script> tags the section emits above its own
       markup are siblings of `root`, not children, so they are collected
       explicitly. The links must come along too: strip() is what adopts them
       into <head>, and a stylesheet dropped here never reaches it — which is how
       the delivery badge, whose CSS is not in data-styles, rendered unstyled. */
       
    const wrapper = root.closest('[id^="shopify-section-"]');
    const fragment = doc.createElement('div');

    if (wrapper) {
      for (const node of wrapper.children) {
        if (['LINK', 'STYLE', 'SCRIPT'].includes(node.tagName)) fragment.append(node.cloneNode(true));
      }
    }

    fragment.append(root.cloneNode(true));
    this.strip(fragment);
    return fragment.innerHTML;
  }

  /* The PDP section's inline <style> carries per-block settings and must keep
     applying, but a <style> is metadata: left in body flow an HTML parser hoists
     it, and the theme editor reads the result as one section nested in another.
     Moving it to <head> ourselves puts it where the parser would have, so the
     rules still apply and the rendered tree stays flat. Runs on every render,
     including cache hits, since the cached markup still contains the node. */
  hoistStyles() {
    document.head.querySelectorAll('style[data-lih-pdp-style]').forEach((node) => node.remove());

    for (const style of this.panel.querySelectorAll('style')) {
      style.dataset.lihPdpStyle = '';
      document.head.appendChild(style);
    }
  }

  /* Drops product page widgets that do not belong on the landing page. Done
     before insertion, so an app scanning for its own placeholder never finds
     one to mount into. */
  strip(scope) {
    /* Metadata tags cannot travel with the injected markup. An HTML parser
       hoists <link>/<style>/<meta> out of body flow, and the theme editor reads
       the resulting tree as one section rendered inside another — reported as an
       HTML error against this page's sections.

       A stylesheet <link> is adopted into <head> before being dropped, rather
       than dropped outright. data-styles lists the ones known at render time,
       but the product template can pull in more — the sticky add-to-cart bar's
       stylesheet is only emitted by some of the PDP section variants — and a
       list that has to be kept in step by hand goes stale silently, leaving
       whatever it missed unstyled. Adopting whatever actually arrives means the
       panel no longer depends on that list being complete.

       The inline <style> is left in place here — it carries the PDP section's
       per-block settings, which exist nowhere else, and this result is cached,
       so removing it would leave a cached tab unstyled on its second visit.
       hoistStyles() moves it at render time. */
    for (const link of scope.querySelectorAll('link[rel="stylesheet"][href]')) {
      this.adoptStylesheet(link.getAttribute('href'));
    }

    scope.querySelectorAll('link, meta, title, base').forEach((node) => node.remove());

    /* Backstop. Nothing above should leave a section wrapper in the fragment,
       but if the product template is ever restructured so one slips through,
       unwrap it rather than letting it nest: a live shopify-section node inside
       this section is what the theme editor reports as broken HTML, and that
       failure surfaces against a neighbouring section rather than this file,
       which makes it expensive to trace. Unwrapping keeps the markup and the id
       is discarded with the element. */
    for (const nested of scope.querySelectorAll('[id^="shopify-section-"]')) {
      nested.replaceWith(...nested.childNodes);
    }

    if (!this.remove) return;
    try {
      scope.querySelectorAll(this.remove).forEach((node) => node.remove());
    } catch (error) {
      /* A malformed selector should not take the whole panel down. */
    }
  }

  /* The tabs sit at the top of the details column, inside the product layout —
     so they move into it after each swap rather than living above the section.
     innerHTML detaches the list but this reference survives, so the same node
     is simply re-inserted.

     This stays inside our own panel: the injected markup no longer carries the
     product page's shopify-section wrapper (load() drops it), so the column the
     row lands in is plain markup, not a second section. That wrapper — not this
     move — was what the theme editor reported as broken HTML. */
  mountTabs() {
    if (!this.list) return;
    const column = this.columnLayout?.matches
      ? this.panel.querySelector(LihProductTabs.COLUMN)
      : null;
    /* Falling back to the panel puts the row above the gallery, which is where
       the mobile design has it. */
    (column || this.panel).prepend(this.list);
    /* Moving a scroller resets scrollLeft, so the selection has to be brought
       back into view after the row lands in its new parent. */
    this.revealTab();
  }

  /**
   * innerHTML never executes <script>. Re-create them so they do — external
   * files once each, inline blocks every swap since they are initialisers.
   *
   * Resolves once those files have run: an inserted src loads asynchronously,
   * so without waiting, the lih:pdp-loaded event dispatched straight after
   * would fire before the very listeners it exists to reach.
   *
   * @returns {Promise<void>} Settled once every newly inserted file has run.
   */
  async activateScripts() {
    const pending = [];

    for (const original of this.panel.querySelectorAll('script')) {
      const src = original.getAttribute('src');
      if (src && LihProductTabs.loaded.has(src)) {
        original.remove();
        continue;
      }
      if (src) LihProductTabs.loaded.add(src);

      const script = document.createElement('script');

      for (const attribute of original.attributes) {
        script.setAttribute(attribute.name, attribute.value);
      }

      script.textContent = original.textContent;

      /* Resolve on error too — a file that fails to load must not hold the
         panel back. */
      if (src) {
        pending.push(
          new Promise((resolve) => {
            script.addEventListener('load', resolve, { once: true });
            script.addEventListener('error', resolve, { once: true });
          })
        );
      }

      original.replaceWith(script);
    }

    await Promise.all(pending);
  }

  /* A dead panel is worse than no panel — send them to the product instead. */
  fallbackLink(tab, url) {
    const title = (tab?.textContent || this.dataset.productLabel || '').trim();
    const action = this.dataset.viewProduct || 'view product';
    const link = document.createElement('a');
    link.className = 'lp-live-in-hoops-product-tabs__fallback';
    link.href = url;
    link.textContent = `${title} — ${action}`;
    return link;
  }
}

if (!customElements.get('lih-product-tabs')) {
  customElements.define('lih-product-tabs', LihProductTabs);
}
